/**
 * Système de gestion centralisée des couleurs - NSIA Assurance Vie
 * Palette :
 *   - Primary:   #001E60
 *   - Secondary: #CF8225
 *   - Tertiary:  #005780
 */

// ============================================
// COULEURS DE MARQUE NIXia (Brand Colors)
// ============================================
export const BRAND_COLORS = {
  primary: '#001E60',
  secondary: '#CF8225',
  tertiary: '#005780',
  primaryDark: '#001748',
  primaryLight: '#0A2B78',
  secondaryDark: '#B7721F',
  secondaryLight: '#E2A44C',
  background: '#111827',
  surface: '#1a1a2e',
};

// ============================================
// ÉCHELLE DE BLEU NIXia (Blue Scale)
// ============================================
export const BLUE_SCALE = {
  50: '#eff6ff',
  100: '#dbeafe',
  200: '#bfdbfe',
  300: '#93c5fd',
  400: '#60a5fa',
  500: '#3590E3',
  600: '#2a75c7',
  700: '#1e5fa8',
  800: '#1e3a8a',
  900: '#1e293b',
};

// ============================================
// COULEURS SÉMANTIQUES (Semantic Colors)
// ============================================
export const SEMANTIC_COLORS = {
  success: '#10b981',
  successLight: '#d1fae5',
  successDark: '#059669',
  error: '#ef4444',
  errorLight: '#fee2e2',
  errorDark: '#dc2626',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  warningDark: '#d97706',
  info: BRAND_COLORS.tertiary,
  infoLight: '#dbeafe',
  infoDark: '#004566',
};

// ============================================
// COULEURS DE TEXTE (Text Colors)
// ============================================
export const TEXT_COLORS = {
  primary: '#111827',
  secondary: '#6b7280',
  tertiary: '#9ca3af',
  disabled: '#d1d5db',
  onPrimary: '#ffffff',
  onSecondary: '#ffffff',
  onDark: '#ffffff',
  onLight: '#111827',
  link: BRAND_COLORS.tertiary,
  linkHover: BRAND_COLORS.primary,
};

// ============================================
// COULEURS DE FOND (Background Colors)
// ============================================
export const BACKGROUND_COLORS = {
  primary: '#ffffff',
  secondary: '#f9fafb',
  tertiary: '#f3f4f6',
  dark: '#1f2937',
  darker: '#111827',
  overlay: 'rgba(0, 0, 0, 0.5)',
  overlayLight: 'rgba(0, 0, 0, 0.25)',
  card: '#ffffff',
  cardHover: '#f9fafb',
};

// ============================================
// COULEURS DE BORDURE (Border Colors)
// ============================================
export const BORDER_COLORS = {
  default: '#e5e7eb',
  light: '#f3f4f6',
  medium: '#d1d5db',
  dark: '#9ca3af',
  focus: BRAND_COLORS.primary,
  error: SEMANTIC_COLORS.error,
  success: SEMANTIC_COLORS.success,
};

// ============================================
// COULEURS INTERACTIVES (Interactive States)
// ============================================
export const INTERACTIVE_COLORS = {
  hover: {
    primary: '#2a75c7',
    secondary: '#a3d685',
    light: '#f3f4f6',
    dark: '#374151',
  },
  active: {
    primary: '#1e5fa8',
    secondary: '#8bc26d',
  },
  disabled: {
    background: '#f3f4f6',
    text: '#d1d5db',
    border: '#e5e7eb',
  },
};

// ============================================
// ÉCHELLE DE GRIS (Gray Scale)
// ============================================
export const GRAY_SCALE = {
  50: '#f9fafb',
  100: '#f3f4f6',
  200: '#e5e7eb',
  300: '#d1d5db',
  400: '#9ca3af',
  500: '#6b7280',
  600: '#4b5563',
  700: '#374151',
  800: '#1f2937',
  900: '#111827',
};

// ============================================
// DÉGRADÉS NIXia (Gradients)
// ============================================
export const GRADIENTS = {
  primary: 'linear-gradient(135deg, #3590E3 0%, #BAF09D 100%)',
  primaryReverse: 'linear-gradient(135deg, #BAF09D 0%, #3590E3 100%)',
  primaryLight: 'linear-gradient(135deg, rgba(53, 144, 227, 0.15) 0%, rgba(186, 240, 157, 0.1) 100%)',
  primaryMedium: 'linear-gradient(135deg, rgba(53, 144, 227, 0.5) 0%, rgba(186, 240, 157, 0.4) 100%)',
  radialPrimary: 'radial-gradient(circle, rgba(53, 144, 227, 0.4) 0%, transparent 70%)',
  radialSecondary: 'radial-gradient(circle, rgba(186, 240, 157, 0.3) 0%, transparent 70%)',
  buttonPrimary: 'linear-gradient(135deg, #3590E3 0%, #2a75c7 100%)',
  buttonSecondary: 'linear-gradient(135deg, #BAF09D 0%, #a3d685 100%)',
  buttonHover: 'linear-gradient(135deg, #2a75c7 0%, #1e5fa8 100%)',

  // ✅ Dashboard dark -cohérent avec la charte admin
  dashboard: '#0A1628',
};

// ============================================
// OMBRES (Shadows)
// ============================================
export const SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  none: 'none',
  primary: '0 10px 25px rgba(53, 144, 227, 0.25)',
  primaryHover: '0 15px 35px rgba(53, 144, 227, 0.4)',
  secondary: '0 10px 25px rgba(186, 240, 157, 0.25)',
};

// ============================================
// COMPOSANTS SPÉCIFIQUES
// ============================================

export const CHAT_COLORS = {
  widget: {
    background: BRAND_COLORS.primary,
    backgroundHover: BRAND_COLORS.primaryDark,
    text: TEXT_COLORS.onPrimary,
    shadow: SHADOWS.primary,
  },
  window: {
    background: BACKGROUND_COLORS.primary,
    header: GRADIENTS.primary,
    headerText: TEXT_COLORS.onPrimary,
  },
  message: {
    user: {
      background: GRADIENTS.primary,
      text: TEXT_COLORS.onPrimary,
    },
    bot: {
      background: BACKGROUND_COLORS.primary,
      text: TEXT_COLORS.primary,
      border: BORDER_COLORS.default,
    },
    error: {
      background: SEMANTIC_COLORS.errorLight,
      text: SEMANTIC_COLORS.error,
    },
  },
  input: {
    background: GRAY_SCALE[50],
    backgroundFocus: BACKGROUND_COLORS.primary,
    border: BORDER_COLORS.default,
    borderFocus: BRAND_COLORS.primary,
    text: TEXT_COLORS.primary,
    placeholder: TEXT_COLORS.secondary,
  },
};

export const BUTTON_COLORS = {
  primary: {
    background: GRADIENTS.buttonPrimary,
    backgroundHover: GRADIENTS.buttonHover,
    text: TEXT_COLORS.onPrimary,
    border: 'transparent',
  },
  secondary: {
    background: GRADIENTS.buttonSecondary,
    backgroundHover: BRAND_COLORS.secondaryDark,
    text: TEXT_COLORS.onDark,
    border: 'transparent',
  },
  success: {
    background: SEMANTIC_COLORS.success,
    backgroundHover: SEMANTIC_COLORS.successDark,
    text: TEXT_COLORS.onDark,
    border: SEMANTIC_COLORS.success,
  },
  outline: {
    background: 'transparent',
    backgroundHover: GRAY_SCALE[50],
    text: BRAND_COLORS.primary,
    border: BRAND_COLORS.primary,
  },
};

// Sidebar -dark navy
export const SIDEBAR_COLORS = {
  background: BRAND_COLORS.primary,
  backgroundHover: 'rgba(0, 87, 128, 0.12)',
  text: '#E8F1FB',
  textSecondary: 'rgba(148, 180, 215, 0.75)',
  itemActive: 'rgba(0, 87, 128, 0.22)',
  itemHover: 'rgba(0, 87, 128, 0.14)',
  border: 'rgba(0, 87, 128, 0.22)',
  borderDark: 'rgba(0, 87, 128, 0.14)',
  badge: BRAND_COLORS.secondary,
  badgeText: '#ffffff',
  badgeOrange: '#f59e0b',
  logoFallbackBg: BRAND_COLORS.secondary,
};

// Header -dark glass
export const HEADER_COLORS = {
  background: 'rgba(10, 20, 36, 0.80)',
  border: 'rgba(0, 87, 128, 0.22)',
  text: '#C8DEF5',
  mobileMenuButton: 'rgba(148, 180, 215, 0.7)',
  buttonHover: 'rgba(0, 87, 128, 0.14)',
};

export const CARD_COLORS = {
  background: BACKGROUND_COLORS.card,
  backgroundHover: BACKGROUND_COLORS.cardHover,
  border: BORDER_COLORS.default,
  shadow: SHADOWS.default,
  shadowHover: SHADOWS.md,
  title: TEXT_COLORS.primary,
  subtitle: TEXT_COLORS.secondary,
  text: TEXT_COLORS.primary,
};

export const FORM_COLORS = {
  input: {
    background: BACKGROUND_COLORS.primary,
    backgroundDisabled: GRAY_SCALE[100],
    border: BORDER_COLORS.default,
    borderFocus: BRAND_COLORS.primary,
    borderError: SEMANTIC_COLORS.error,
    text: TEXT_COLORS.primary,
    placeholder: TEXT_COLORS.secondary,
  },
  label: {
    text: TEXT_COLORS.primary,
    required: SEMANTIC_COLORS.error,
  },
};

export const MODAL_COLORS = {
  overlay: BACKGROUND_COLORS.overlay,
  background: BACKGROUND_COLORS.primary,
  header: {
    background: GRADIENTS.primaryLight,
    text: TEXT_COLORS.primary,
    border: BORDER_COLORS.default,
  },
};

export const BADGE_COLORS = {
  primary: {
    background: 'rgba(53, 144, 227, 0.1)',
    text: BRAND_COLORS.primary,
  },
  secondary: {
    background: 'rgba(186, 240, 157, 0.2)',
    text: BRAND_COLORS.secondaryDark,
  },
  success: {
    background: SEMANTIC_COLORS.successLight,
    text: SEMANTIC_COLORS.successDark,
  },
  error: {
    background: SEMANTIC_COLORS.errorLight,
    text: SEMANTIC_COLORS.errorDark,
  },
};

// ============================================
// BANNIÈRES (Banners)
// ============================================
export const BANNER_COLORS = {
  trial: {
    normal: {
      background: '#e8f4fd',
      border: '#3590E3',
      text: '#2d3748',
      icon: '#3590E3',
      button: '#3590E3',
      buttonHover: '#2a75c7',
    },
    warning: {
      background: '#fff5e6',
      border: '#f59e0b',
      text: '#78350f',
      icon: '#f59e0b',
      button: '#f59e0b',
      buttonHover: '#d97706',
    },
    expired: {
      background: '#fef2f2',
      border: '#ef4444',
      text: '#7f1d1d',
      icon: '#ef4444',
      button: '#ef4444',
      buttonHover: '#dc2626',
    },
  },
};

// ============================================
// FONCTIONS UTILITAIRES
// ============================================
export const hexToRgba = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const withOpacity = (color, opacity) => {
  if (color.startsWith('#')) return hexToRgba(color, opacity);
  return color;
};

export const radialGradient = (color, opacity = 0.4) => {
  return `radial-gradient(circle, ${hexToRgba(color, opacity)}, transparent)`;
};

// ============================================
// EXPORT PAR DÉFAUT
// ============================================
export default {
  BRAND_COLORS,
  SEMANTIC_COLORS,
  TEXT_COLORS,
  BACKGROUND_COLORS,
  BORDER_COLORS,
  INTERACTIVE_COLORS,
  GRAY_SCALE,
  BLUE_SCALE,
  GRADIENTS,
  SHADOWS,
  BANNER_COLORS,
  CHAT_COLORS,
  BUTTON_COLORS,
  SIDEBAR_COLORS,
  HEADER_COLORS,
  CARD_COLORS,
  FORM_COLORS,
  MODAL_COLORS,
  BADGE_COLORS,
  hexToRgba,
  withOpacity,
  radialGradient,
};
